import { PAYLOAD_SAFE_PARAMS } from './payloadSafeParams';

/**
 * What a binding may do when a component's `_action.*` handlers run on a
 * partner module's signal instead of a visitor's click.
 */
export interface OutputBindingPolicy {
  /** Action types a binding may run. */
  readonly actionTypes: ReadonlySet<string>;
  /** Top-level params per action type that may read `{{event.*}}`. */
  readonly payloadSafeParams: Readonly<Record<string, readonly string[]>>;
  /**
   * Action types dispatched under another name when a binding runs them, so
   * the dispatcher can hold a stricter handler for a partner signal than for a
   * visitor's click. A dispatcher without the stricter handler drops the
   * action.
   */
  readonly dispatchAs?: Readonly<Record<string, string>>;
}

/**
 * `navigation.advanceStep` as a binding runs it: it moves to the next step,
 * and does nothing when the step would submit the flow or hand off to a
 * redirect. The dispatcher registers it next to `navigation.advanceStep`.
 */
export const ADVANCE_STEP_WITHOUT_SUBMIT_ACTION =
  'navigation.advanceStepWithoutSubmit';

/**
 * Actions a `platform.hostSlot` output may run. Actions that leave the page or
 * submit data are not on the list, so a binding cannot turn a partner signal
 * into an exit or a submission. `navigation.advanceStep` is on it because the
 * policy runs it as {@link ADVANCE_STEP_WITHOUT_SUBMIT_ACTION}, which refuses
 * a step that would submit or redirect. Every type on the list has a handler
 * in the dispatcher, so a binding that passes is one that runs; an action the
 * dispatcher does not register would be dropped without telling the module.
 * Dynamic Flows holds the same list in
 * `application/config/HostSlotOutputActions.ts`. Keep the two in lockstep.
 */
export const HOST_SLOT_OUTPUT_ACTION_TYPES: ReadonlySet<string> = new Set([
  'navigation.advanceStep',
  'navigation.goToStep',
  'navigation.goBack',
  'modal.open',
  'modal.close',
  'formState.set',
  'formState.clear',
]);

export const HOST_SLOT_OUTPUT_POLICY: OutputBindingPolicy = {
  actionTypes: HOST_SLOT_OUTPUT_ACTION_TYPES,
  payloadSafeParams: PAYLOAD_SAFE_PARAMS,
  dispatchAs: {
    'navigation.advanceStep': ADVANCE_STEP_WITHOUT_SUBMIT_ACTION,
  },
};
